import { createFileRoute, Link } from "@tanstack/react-router";
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { forwardAuthorization, initializeWorker } from '../../storageWorker/handles.js';
import listColumns from '../models/list';
import { messages } from '../../storageWorker/messages.js';
import { getCoreRowModel, useReactTable } from '@tanstack/react-table';
import noneImage from '../../../images/new-icons/Icon_80_list.svg';
import ConfirmModal from "../components/ConfirmModal.jsx";
const defaultName = "My Georgia O'Keeffe List";
const backend = "https://access-okeeffe.pockethost.io";
const AllListsRow = (row, key, removeList) => {
  const cells = row.getVisibleCells();
  const visibleValues = cells.reduce((acc, cur) => {
    const key = cur.column.id;
    acc[key] = cur.getValue();
    return acc;
  }, {});
  const {
    id,
    count,
    description,
    name,
    createdDate,
    updatedDate,
    shared,
    readOnly
  } = visibleValues;
  let src = '/assets/' + noneImage;
  // TODO: first item image in storage worker (use cursor)

  // if (items.length && items.at(0) && items.at(0).imageUrl) {
  //   const item = items.at(0)
  //   const terminated = item.imageUrl?.endsWith('/') ? item.imageUrl : item.imageUrl + '/'
  //   src = new URL('full/,242/0/default.jpg', terminated).href
  // }

  const createdFormatter = new Intl.DateTimeFormat("en-US", {
    month: 'long',
    day: 'numeric',
    year: 'numeric'
  });
  const updatedFormatter = new Intl.DateTimeFormat("en-US", {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: 'numeric'
  });
  return <li className="list-item" key={key}>
        <div className="list-item-section">
          <div className="list-item-section-image">
              <figure className="image"><img className="" src={src} alt="" loading="eager" />
              </figure>
          </div>
          <div className="column list-item-section-description">
            <div className="is-flex is-align-items-center">
              <Link to='/$listId' params={{
            listId: id
          }} className="btn-text btn-lg px-0 mr-3">{name ?? <em>(Untitled list)</em>}</Link>
              <span className={`tag is-read-only ${readOnly ? '' : 'is-hidden'}`}>Read Only</span>
              <span className={`tag is-shared ${shared ? '' : 'is-hidden'}`}>Shared</span>
            </div>
            <div className="list-item-description">{description}</div>
            <div className="list-info">
              <div className="left-column">
                <div className="left-column-div">
                  <span>{count} items </span>
                  <div className="vr is-hidden-mobile"></div>
                </div>
                <div className="left-column-div">
                  <span>Created {createdFormatter.format(createdDate)} </span>
                  {updatedDate ? <div className="vr"></div> : ''}
                  <span>{updatedDate ? `Updated ${updatedFormatter.format(updatedDate).replace(' at ', ', ')}` : ''}</span>
                </div>
              </div>
            </div>
          </div>
          <div className="column is-flex is-justify-content-flex-end is-align-items-center">
            <div className="is-right">
              <button type="button" className="btn-icon-only icon-lg btn-close" onClick={() => removeList(id)}><span className="icon icon-close-large"></span>
                <span className="sr-only">Remove list</span></button>
            </div>
          </div>
        </div>
      </li>;
};
const requestData = async port => {
  return new Promise((resolve, reject) => {
    const handleRequest = event => {
      const {
        data: eventData
      } = event;
      const {
        type,
        data
      } = eventData;
      if (type === messages.LOAD_LISTS) {
        port.removeEventListener('message', handleRequest);
        resolve(data);
      } else {
        reject("Did not receive expected backend response!");
      }
    };
    port.onmessage = handleRequest;
    port.postMessage({
      type: messages.LOAD_LISTS
    });
  });
};
const ListsPage = () => {
  const {
    initialData,
    storageWorker,
    workerPort
  } = Route.useLoaderData();
  const [data, setData] = useState(initialData);
  const [ready, setReady] = useState(false);
  const [creating, setCreating] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [nameToDelete, setNameToDelete] = useState('');
  const broadcast = useMemo(() => new BroadcastChannel('cr-lists-storage'));
  const nameInput = useRef(null);
  const descriptionInput = useRef(null);
  const table = useReactTable({
    data,
    columns: listColumns,
    getCoreRowModel: getCoreRowModel()
  });
  const requestStorageData = () => {
    workerPort.postMessage({
      type: messages.LOAD_LISTS
    });
  };
  const messageHandler = event => {
    const {
      data: eventData
    } = event;
    const {
      type,
      data
    } = eventData;
    switch (type) {
      case messages.WORKER_READY:
        setReady(true);
        break;
      case messages.LOAD_LISTS:
        setData(data);
        break;
      case messages.DELETE_LIST:
      case messages.SAVE_LIST:
        // TODO: ok, no-op -- consider using this to UI signal save finish
        break;
      default:
        console.warn(`Got an unrecognized message type, ${type}`);
        return;
    }
  };
  const handleBroadcast = event => {
    const {
      data
    } = event;
    const {
      type
    } = data;
    switch (type) {
      case messages.STORAGE_UPDATED:
        requestStorageData();
        break;
      default:
        console.warn(`Unknown broadcast ${type}`);
    }
  };

  // Storage worker init: add listener hook
  useEffect(() => {
    setReady(true);
    workerPort.onmessage = messageHandler;
    forwardAuthorization(backend, workerPort);
  }, [workerPort]);
  useEffect(() => {
    if (!ready) return;

    // Attach to the storage broadcast for updates from other tabs
    broadcast.onmessage = handleBroadcast;
  }, [ready]);

  /**
   * @function makeNewList
   *
   * Creates a new list, using the default name if none exists
   *
   **/
  const makeNewList = (name = defaultName, items = [], description = '') => {
    return {
      id: `${crypto.randomUUID()}`,
      name,
      description,
      items
    };
  };
  const createNewList = () => {
    if (!nameInput.current || !descriptionInput.current) return;
    const name = nameInput.current.value ? nameInput.current.value : defaultName;
    const description = descriptionInput.current.value;
    const list = makeNewList(name, [], description);
    data.push(list);
    workerPort.postMessage({
      type: messages.SAVE_LIST,
      list
    });
    setData([...data]);
    setCreating(false);
    nameInput.current.value = '';
    descriptionInput.current.value = '';
  };
  const showDeletingModal = id => {
    setItemToDelete(id);
    const list = data.find(list => list.id === id);
    setNameToDelete(list.name);
  };
  const deleteList = () => {
    workerPort.postMessage({
      type: messages.DELETE_LIST,
      id: itemToDelete
    });
    setItemToDelete(null);
    setNameToDelete('');
  };
  return <section className="section list-page">
            <div className="list-info border-bottom mt-4 pb-3">
              <div className="right-column">
                <button type="button" onClick={() => setCreating(true)} className="button js-modal-trigger" data-target="create-list-modal"><span className="icon icon-plus"></span>Create a New List</button>

              </div>
            </div>
            <ul className="list">
              {table.getRowModel().rows.map((row, i) => AllListsRow(row, i, showDeletingModal))}
            </ul>
            <ConfirmModal confirming={itemToDelete} cancel={() => setItemToDelete(null)} buttonText="Delete" handleConfirm={() => deleteList()} message={<p>Are you sure you want to delete "<strong>{nameToDelete}</strong>"?</p>} />
            <div id="create-list-modal" className={`modal ${creating ? 'is-active' : ''}`}>
              <div className="modal-background"></div>
              <div className="modal-card">
                <header className="modal-card-head">
                  <h1 className="modal-card-title">
                    CREATE NEW LIST</h1>
                  <button onClick={() => setCreating(false)} className="btn-icon-only icon-lg btn-close" aria-label="close">
                    <span className="icon icon-close-large"></span><span className="sr-only">Close this
                      dialog</span>
                  </button>
                </header>
                <section className="modal-card-body">
                  <div className="field">
                    <label className="label">List Name:</label>
                    <div className="control">
                      <input defaultValue={defaultName} ref={nameInput} className="input" type="text" placeholder="List Name" />
                    </div>
                  </div>
                  <div className="field">
                    <label className="label">List Description (optional):</label>
                    <div className="control">
                      <input ref={descriptionInput} className="input" type="text" placeholder="List Description" />
                    </div>
                  </div>
                </section>
                <footer className="modal-card-foot">
                  <div className="is-flex ml-auto">
                    <button className="button" onClick={() => setCreating(false)}>Cancel</button>
                    <button className="btn-solid" onClick={createNewList}>Create</button>
                  </div>
                </footer>
              </div>
            </div>

          </section>;
};
export const Route = createFileRoute('/')({
  component: ListsPage,
  beforeLoad: async () => {
    // NB: Overrides webpack 5's publicDir setting for the worker bundle
    __webpack_public_path__ = "/assets/";
    const storageWorker = new Worker(new URL('../../storageWorker/index.mjs', import.meta.url));
    const workerPort = await initializeWorker(storageWorker);
    return {
      storageWorker,
      workerPort
    };
  },
  loader: async ({
    context: {
      storageWorker,
      workerPort
    }
  }) => {
    const initialData = await requestData(workerPort);
    return {
      initialData,
      storageWorker,
      workerPort
    };
  },
  id: 'lists-index'
});
if (import.meta.hot) {
  import.meta.hot.accept(newModule => {
    if (newModule && newModule.Route && typeof newModule.Route.clone === 'function') {
      newModule.Route.clone(Route);
    }
  });
}